import React, { useState, useEffect, useRef, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserShield, faMedal, faGem, faUser, faBriefcase, faHeadset } from '@/icons';
import { AuthContext } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { debugError } from '../utils/debug';
import { CustomTooltip } from './CustomTooltip';
import { UserSelectionModal } from './UserSelectionModal';
import { SpinnerOverlay } from './SpinnerOverlay';
import { authApiService } from '../services/authApiService';

/**
 * Combined Admin FAB with submenu (Admin/Root only)
 * Shows subscription toggle and business role toggle
 */
export const AdminFAB: React.FC = () => {
    // Use useContext directly to check if context is available
    const authContext = useContext(AuthContext);

    // Don't render if AuthProvider is not available
    if (!authContext) {
        return null;
    }

    const { user, getCurrentUser, updateUserTeam } = authContext;
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();


    const [showSubmenu, setShowSubmenu] = useState(false);
    const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);
    const [isSwitchingSubscription, setIsSwitchingSubscription] = useState(false);
    const [isSwitchingBusinessRole, setIsSwitchingBusinessRole] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const fabRef = useRef<HTMLDivElement>(null);

    // Check if user is Admin or Root
    const isAdminOrRoot = user?.role === 'Admin' || user?.role === 'Root';

    // Check if user is Root (for impersonation feature)
    const isRoot = user?.role === 'Root';


    // State for subscription type override (admin/root only)
    const [subscriptionTypeOverride, setSubscriptionTypeOverride] = useState<string | null>(() => {
        try {
            return sessionStorage.getItem('subscription_type_override');
        } catch {
            return null;
        }
    });

    // Get actual subscription type (use override if set, otherwise use actual subscription type)
    const actualSubscriptionType = subscriptionTypeOverride || user?.team?.subscriptionType;
    const isPro = actualSubscriptionType?.toLowerCase() === 'pro';
    const subscriptionLabel = isPro ? 'Pro' : 'Standard';

    // Get actual business role (from user object, which should respect JWT claims)
    const actualBusinessRole = user?.businessRole || '';
    const isAttorney = actualBusinessRole?.toLowerCase() === 'attorney';
    const businessRoleLabel = isAttorney ? 'Attorney' : 'Support Staff';

    // Determine which icon to show on the FAB button based on current business role
    const fabIcon = isAttorney ? faBriefcase : (actualBusinessRole?.toLowerCase() === 'support staff' ? faHeadset : faUserShield);


    // Toggle subscription type override (admin/root only)
    const toggleSubscriptionType = async () => {
        const newType = actualSubscriptionType?.toLowerCase() === 'pro' ? 'standard' : 'pro';
        setIsSwitchingSubscription(true);
        setShowSubmenu(false);

        try {
            const response = await authApiService.toggleSubscriptionType(newType);

            if (response.success) {
                // Clear the sessionStorage override since the JWT now has the correct value
                try {
                    sessionStorage.removeItem('subscription_type_override');
                    setSubscriptionTypeOverride(null);
                } catch (e) {
                    console.error('Failed to clear subscription type override:', e);
                }

                // Refresh user state to get updated team info with new subscription type
                // The GetCurrentUser endpoint now uses subscription_type from JWT token
                // Add a small delay to ensure cookies are set by the server
                await new Promise(resolve => setTimeout(resolve, 100));
                await getCurrentUser();

                // Dispatch event to notify other components
                window.dispatchEvent(new CustomEvent('subscriptionTypeChanged', { detail: { subscriptionType: newType } }));
            }
        } catch (error) {
            console.error('Failed to toggle subscription type:', error);
            // Fallback to sessionStorage override if API call fails
            setSubscriptionTypeOverride(newType);
            try {
                sessionStorage.setItem('subscription_type_override', newType);
                window.dispatchEvent(new CustomEvent('subscriptionTypeChanged', { detail: { subscriptionType: newType } }));
            } catch (e) {
                console.error('Failed to save subscription type override:', e);
            }
        } finally {
            setIsSwitchingSubscription(false);
        }
    };

    // Toggle business role (admin/root only)
    const toggleBusinessRole = async () => {
        const newRole = isAttorney ? 'Support Staff' : 'Attorney';
        setIsSwitchingBusinessRole(true);
        setShowSubmenu(false);

        try {
            const response = await authApiService.toggleBusinessRole(newRole);

            if (response.success) {
                // Refresh user state to get updated business role
                // Add a small delay to ensure cookies are set by the server
                await new Promise(resolve => setTimeout(resolve, 100));
                await getCurrentUser();

                // Dispatch event to notify other components
                window.dispatchEvent(new CustomEvent('businessRoleChanged', { detail: { businessRole: newRole } }));
            }
        } catch (error) {
            console.error('Failed to toggle business role:', error);
        } finally {
            setIsSwitchingBusinessRole(false);
        }
    };


    const handleOpenUserSelection = () => {
        setShowUserSelectionModal(true);
        setShowSubmenu(false);
    };

    const handleSelectUser = async (userId: string) => {
        try {
            const response = await authApiService.impersonateUser(userId);
            if (response.success && response.data) {
                // Update auth state by fetching current user (tokens are in HTTP-only cookies)
                await getCurrentUser();

                // Navigate to home page to refresh the app with new user context
                navigate('/');

                // Reload the page to ensure all components pick up the new user
                window.location.reload();
            } else {
                debugError('Failed to impersonate user');
            }
        } catch (err) {
            debugError('Error impersonating user:', err);
        }
    };

    // Close submenu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (fabRef.current && !fabRef.current.contains(event.target as Node)) {
                setShowSubmenu(false);
            }
        };

        if (showSubmenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSubmenu]);

    // Don't render if user is not admin or root
    if (!isAdminOrRoot) {
        return null;
    }

    // Determine what the subscription will change to (opposite of current)
    const targetSubscriptionLabel = isPro ? 'Emulate Standard Subscription' : 'Emulate Pro Subscription';
    const targetSubscriptionIcon = isPro ? faMedal : faGem;

    // Icons for current subscription type (for tooltip)
    const currentSubscriptionIcon = isPro ? faGem : faMedal;

    // Determine what the business role will change to (opposite of current)
    const targetBusinessRoleLabel = isAttorney ? 'Emulate Support Staff' : 'Emulate Attorney';
    const targetBusinessRoleIcon = isAttorney ? faHeadset : faBriefcase;

    const submenuItems = [
        // Root-only: Emulate User
        ...(isRoot ? [{
            label: 'Emulate User...',
            onClick: handleOpenUserSelection,
            icon: faUser,
        }] : []),
        {
            label: targetSubscriptionLabel,
            onClick: toggleSubscriptionType,
            icon: targetSubscriptionIcon, // Show icon for what it will change to
        },
        {
            label: targetBusinessRoleLabel,
            onClick: toggleBusinessRole,
            icon: targetBusinessRoleIcon, // Show icon for what it will change to
        },
    ];

    return (
        <>
            <div ref={fabRef} style={{ position: 'fixed', bottom: theme.spacing.md, left: theme.spacing.md, zIndex: 1000 }}>
                {/* Submenu */}
                {showSubmenu && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: '36px',
                            left: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing.xs,
                            minWidth: '240px',
                            padding: theme.spacing.xs,
                            backgroundColor: theme.colors.surface,
                            border: `1px solid ${theme.colors.border.default}`,
                            borderRadius: theme.borderRadius.md,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        {submenuItems.map((item, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={item.onClick}
                                style={{
                                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    borderRadius: theme.borderRadius.sm,
                                    color: theme.colors.text.primary,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.sm,
                                    fontSize: theme.typography.fontSize.sm,
                                    textAlign: 'left',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <FontAwesomeIcon icon={item.icon} style={{ fontSize: theme.typography.fontSize.sm }} />
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </div>
                )}

                {/* Main FAB Button */}
                <CustomTooltip
                    content={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FontAwesomeIcon icon={fabIcon} style={{ fontSize: '12px' }} />
                            <span>{businessRoleLabel}</span>
                            <span>/</span>
                            <FontAwesomeIcon icon={currentSubscriptionIcon} style={{ fontSize: '12px' }} />
                            <span>{subscriptionLabel}</span>
                        </div>
                    }
                    position="right"
                    show={showTooltip && !showSubmenu}
                >
                    <button
                        type="button"
                        onClick={() => setShowSubmenu(!showSubmenu)}
                        style={{
                            width: '28px',
                            height: '28px',
                            padding: 0,
                            border: 'none',
                            borderRadius: '50%',
                            backgroundColor: isPro ? theme.colors.primary : theme.colors.surface,
                            color: isPro ? theme.colors.text.inverse : theme.colors.text.secondary,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s ease',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2), 0 2px 6px rgba(0, 0, 0, 0.15)';
                            setShowTooltip(true);
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)';
                            setShowTooltip(false);
                        }}
                    >
                        <FontAwesomeIcon
                            icon={fabIcon}
                            style={{ fontSize: '14px' }}
                        />
                    </button>
                </CustomTooltip>
            </div>

            {/* User Selection Modal */}
            <UserSelectionModal
                isOpen={showUserSelectionModal}
                onClose={() => setShowUserSelectionModal(false)}
                onSelectUser={handleSelectUser}
            />

            {/* Subscription Switching Overlay */}
            <SpinnerOverlay
                isVisible={isSwitchingSubscription}
                message="Switching subscriptions..."
            />

            {/* Business Role Switching Overlay */}
            <SpinnerOverlay
                isVisible={isSwitchingBusinessRole}
                message="Switching roles..."
            />
        </>
    );
};
